/* ==========================================================================
   GLOWSTONE — Configurator + upload field
   <glowstone-upload>       validated photo upload with preview
   <glowstone-configurator> size/finish selection, variant resolution,
                            live preview, add to cart through /cart/add
   Product data is serialized by Liquid; no IDs are hard-coded here.
   ========================================================================== */

(function () {
  'use strict';

  var ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  var MAX_FILE_BYTES = 20 * 1024 * 1024;

  /* ------------------------------------------------------------------
     Upload field
  ------------------------------------------------------------------ */
  if (!customElements.get('glowstone-upload')) {
    customElements.define(
      'glowstone-upload',
      class GlowstoneUpload extends HTMLElement {
        connectedCallback() {
          if (this.dataset.gsInit) return;
          this.dataset.gsInit = 'true';

          this.input = this.querySelector('input[type="file"]');
          this.drop = this.querySelector('.gs-upload__drop');
          this.thumb = this.querySelector('.gs-upload__thumb');
          this.filename = this.querySelector('.gs-upload__filename');
          this.errorEl = this.querySelector('.gs-upload__error');
          this.previewUrl = null;

          if (!this.input) return;

          this.input.addEventListener('change', this.onChange.bind(this));

          var replaceBtn = this.querySelector('[data-gs-upload-replace]');
          var removeBtn = this.querySelector('[data-gs-upload-remove]');
          if (replaceBtn) replaceBtn.addEventListener('click', () => this.input.click());
          if (removeBtn) removeBtn.addEventListener('click', () => this.clear());

          if (this.drop) {
            this._onDragEnter = (event) => { event.preventDefault(); this.drop.classList.add('gs-dragover'); };
            this._onDragLeave = (event) => { event.preventDefault(); this.drop.classList.remove('gs-dragover'); };
            this._onDrop = (event) => {
              event.preventDefault();
              this.drop.classList.remove('gs-dragover');
              if (!event.dataTransfer || !event.dataTransfer.files.length) return;
              this.input.files = event.dataTransfer.files;
              this.onChange();
            };
            this.drop.addEventListener('dragenter', this._onDragEnter);
            this.drop.addEventListener('dragover', this._onDragEnter);
            this.drop.addEventListener('dragleave', this._onDragLeave);
            this.drop.addEventListener('drop', this._onDrop);
          }
        }

        disconnectedCallback() {
          if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = null;
          }
          delete this.dataset.gsInit;
        }

        onChange() {
          var file = this.input.files && this.input.files[0];
          if (!file) {
            this.clear();
            return;
          }

          var error = this.validate(file);
          if (error) {
            this.input.value = '';
            this.setError(error);
            return;
          }

          this.setError(null);
          if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
          this.previewUrl = URL.createObjectURL(file);

          if (this.thumb) this.thumb.src = this.previewUrl;
          if (this.filename) this.filename.textContent = file.name;
          this.classList.add('gs-has-file');

          this.dispatchEvent(
            new CustomEvent('gs:upload:change', {
              bubbles: true,
              detail: { file: file, url: this.previewUrl },
            })
          );
        }

        validate(file) {
          if (ACCEPTED_TYPES.indexOf(file.type) === -1) {
            return this.dataset.errorType || 'Please choose a JPG, PNG, or WebP image.';
          }
          if (file.size > MAX_FILE_BYTES) {
            return this.dataset.errorSize || 'That file is larger than 20 MB. Please choose a smaller image.';
          }
          return null;
        }

        setError(message) {
          if (!this.errorEl) return;
          if (message) {
            this.errorEl.textContent = message;
            this.classList.add('gs-has-error');
          } else {
            this.errorEl.textContent = '';
            this.classList.remove('gs-has-error');
          }
        }

        clear() {
          this.input.value = '';
          if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = null;
          }
          this.classList.remove('gs-has-file');
          this.setError(null);
          this.dispatchEvent(
            new CustomEvent('gs:upload:change', { bubbles: true, detail: { file: null, url: null } })
          );
        }
      }
    );
  }

  /* ------------------------------------------------------------------
     Configurator
  ------------------------------------------------------------------ */
  if (!customElements.get('glowstone-configurator')) {
    customElements.define(
      'glowstone-configurator',
      class GlowstoneConfigurator extends HTMLElement {
        connectedCallback() {
          if (this.dataset.gsInit) return;
          this.dataset.gsInit = 'true';

          var dataEl = this.querySelector('[data-gs-products]');
          if (!dataEl) return;
          try {
            this.catalog = JSON.parse(dataEl.textContent);
          } catch (error) {
            console.error('Glowstone configurator: invalid product data', error);
            return;
          }
          if (!this.catalog.length) return;

          this.form = this.querySelector('form');
          this.idInput = this.form.querySelector('input[name="id"]');
          this.sizeProp = this.form.querySelector('[data-gs-prop-size]');
          this.finishProp = this.form.querySelector('[data-gs-prop-finish]');
          this.cropProp = this.form.querySelector('[data-gs-prop-crop]');
          this.priceEls = this.querySelectorAll('[data-gs-price]');
          this.availabilityEl = this.querySelector('[data-gs-availability]');
          this.submitButtons = this.querySelectorAll('[data-gs-submit]');
          this.errorEl = this.querySelector('[data-gs-error]');
          this.statusEl = this.querySelector('[data-gs-status]');
          this.finishValueEl = this.querySelector('[data-gs-finish-value]');
          this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

          this.hasPhoto = false;
          this.submitting = false;
          this.orientation = 'portrait';

          var cropperEl = this.querySelector('[data-gs-cropper]');
          this.cropper = cropperEl ? new window.GlowstoneCropper(cropperEl) : null;

          this.bindSelection();
          this.bindUpload();
          this.bindOrientation();
          this.form.addEventListener('submit', this.onSubmit.bind(this));

          /* Allow deep links to preselect a size (?gs-size=a|b) */
          try {
            var preselect = new URLSearchParams(window.location.search).get('gs-size');
            if (preselect) {
              var radio = this.querySelector('[data-gs-size-input][data-gs-size-key="' + preselect + '"]');
              if (radio) radio.checked = true;
            }
          } catch (error) {
            /* no-op */
          }

          this.syncFromInputs();
        }

        /* ---------------- selection ---------------- */
        bindSelection() {
          this.addEventListener('change', (event) => {
            if (event.target.matches('[data-gs-size-input]') || event.target.matches('[data-gs-finish-input]')) {
              this.syncFromInputs();
            }
          });
        }

        activeEntry() {
          var checked = this.querySelector('[data-gs-size-input]:checked');
          var key = checked ? checked.dataset.gsSizeKey : this.catalog[0].key;
          return (
            this.catalog.find(function (entry) {
              return entry.key === key;
            }) || this.catalog[0]
          );
        }

        activeFinish(entry) {
          var group = this.querySelector('[data-gs-finish-group="' + entry.key + '"]');
          if (!group) return null;
          var checked = group.querySelector('[data-gs-finish-input]:checked');
          if (checked) return checked.value;
          var first = group.querySelector('[data-gs-finish-input]');
          if (first) {
            first.checked = true;
            return first.value;
          }
          return null;
        }

        syncFromInputs() {
          var entry = this.activeEntry();

          /* Reveal only the finish group for the active size */
          this.querySelectorAll('[data-gs-finish-group]').forEach((group) => {
            group.hidden = group.dataset.gsFinishGroup !== entry.key;
          });

          var finish = this.activeFinish(entry);
          var variant = this.resolveVariant(entry, finish);

          if (this.idInput) {
            this.idInput.value = variant ? variant.id : '';
            this.idInput.disabled = !variant;
          }

          /* Size / finish as line item properties only when the variant
             itself does not encode them */
          if (this.sizeProp) {
            this.sizeProp.disabled = !entry.propertyMode;
            this.sizeProp.value = entry.sizeLabel;
          }
          if (this.finishProp) {
            this.finishProp.disabled = !entry.propertyMode || !finish;
            this.finishProp.value = finish || '';
          }

          /* Price + availability */
          var available = !!(variant && variant.available);
          this.priceEls.forEach(function (el) {
            el.textContent = '';
          });
          if (this.availabilityEl) {
            this.availabilityEl.textContent = variant
              ? available
                ? this.dataset.textInStock || ''
                : this.dataset.textSoldOut || 'Sold out'
              : this.dataset.textUnavailable || 'Unavailable';
          }

          if (this.finishValueEl) this.finishValueEl.textContent = finish || '';

          if (this.cropper) {
            this.cropper.setRatio(this.orientedRatio(entry.ratio), entry.sizeLabel);
          }

          this.available = available;
          this.updateSubmitState();
        }

        resolveVariant(entry, finish) {
          if (!entry.variants || !entry.variants.length) return null;

          var matches = entry.variants.filter(function (variant) {
            if (entry.sizeOptionIndex != null && variant.options[entry.sizeOptionIndex] !== entry.sizeValue) {
              return false;
            }
            if (entry.finishOptionIndex != null && finish && variant.options[entry.finishOptionIndex] !== finish) {
              return false;
            }
            return true;
          });

          if (!matches.length) return null;
          var availableMatch = matches.find(function (variant) {
            return variant.available;
          });
          return availableMatch || matches[0];
        }

        updateSubmitState() {
          var ready = this.available && this.hasPhoto && !this.submitting;
          this.submitButtons.forEach((button) => {
            button.setAttribute('aria-disabled', ready ? 'false' : 'true');
            var label = button.querySelector('[data-gs-submit-label]');
            if (!label) return;
            if (!this.available) {
              label.textContent = this.dataset.textSoldOut || 'Sold out';
            } else if (!this.hasPhoto) {
              label.textContent = this.dataset.textNeedsPhoto || 'Upload a photo to continue';
            } else {
              label.textContent = this.dataset.textAddToCart || 'Add to cart';
            }
          });
        }

        /* ---------------- orientation ---------------- */
        bindOrientation() {
          this.querySelectorAll('[data-gs-orient]').forEach((btn) => {
            btn.addEventListener('click', () => {
              this.orientation = btn.dataset.gsOrient;
              this.querySelectorAll('[data-gs-orient]').forEach(function (b) {
                b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
              });
              this.syncFromInputs();
            });
          });
        }

        orientedRatio(ratio) {
          var parts = (ratio || '1 / 1').split('/');
          if (parts.length !== 2) return ratio;
          var a = parts[0].trim(), b = parts[1].trim();
          if (a === b) return ratio;
          if (this.orientation === 'landscape') return b + ' / ' + a;
          return a + ' / ' + b;
        }

        /* ---------------- upload + cropper ---------------- */
        bindUpload() {
          this.addEventListener('gs:upload:change', (event) => {
            var detail = event.detail || {};
            this.hasPhoto = !!detail.file;

            if (this.cropper) {
              if (detail.url) this.cropper.setImage(detail.url);
              else this.cropper.removeImage();
            }

            this.syncCropData();
            this.updateSubmitState();
            this.clearError();
          });
        }

        syncCropData() {
          if (!this.cropProp || !this.cropper) return;
          var data = this.cropper.getData();
          if (!data) { this.cropProp.disabled = true; this.cropProp.value = ''; return; }
          this.cropProp.disabled = false;
          this.cropProp.value = JSON.stringify(data);
        }

        /* ---------------- submit ---------------- */
        onSubmit(event) {
          event.preventDefault();
          if (this.submitting) return;

          if (!this.hasPhoto) {
            this.showError(this.dataset.textNeedsPhoto || 'Upload a photo to continue.');
            var upload = this.querySelector('glowstone-upload input[type="file"]');
            if (upload) upload.focus();
            return;
          }
          if (!this.available) {
            this.showError(this.dataset.textSoldOut || 'This combination is sold out.');
            return;
          }

          this.syncCropData();
          this.submitting = true;
          this.updateSubmitState();
          this.setStatus(this.dataset.textAdding || 'Adding to cart…');
          this.clearError();

          var formData = new FormData(this.form);
          if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
            formData.append(
              'sections',
              this.cart
                .getSectionsToRender()
                .map(function (section) {
                  return section.id;
                })
                .join(',')
            );
            formData.append('sections_url', window.location.pathname);
            if (typeof this.cart.setActiveElement === 'function') {
              this.cart.setActiveElement(document.activeElement);
            }
          }

          fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          })
            .then(function (response) {
              return response.json();
            })
            .then((response) => {
              if (response.status) {
                this.showError(response.description || response.message || 'Something went wrong. Please try again.');
                return;
              }
              this.setStatus(this.dataset.textAdded || 'Added to cart.');
              if (this.cart && typeof this.cart.renderContents === 'function') {
                this.cart.classList.remove('is-empty');
                this.cart.renderContents(response);
              } else {
                window.location = window.routes.cart_url;
              }
            })
            .catch(() => {
              this.showError('Network error. Please check your connection and try again.');
            })
            .finally(() => {
              this.submitting = false;
              this.updateSubmitState();
            });
        }

        showError(message) {
          if (this.errorEl) {
            this.errorEl.textContent = message;
            this.errorEl.hidden = false;
          }
          this.setStatus('');
        }

        clearError() {
          if (this.errorEl) {
            this.errorEl.textContent = '';
            this.errorEl.hidden = true;
          }
        }

        setStatus(message) {
          if (this.statusEl) this.statusEl.textContent = message;
        }

        disconnectedCallback() {
          delete this.dataset.gsInit;
        }
      }
    );
  }
})();
